import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Droplets, Mail, Lock, User, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function AuthPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, login, register, loginWithGoogle } = useAuth();
    const hasProcessed = useRef(false);
    
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [registerData, setRegisterData] = useState({ email: '', password: '', name: '' });

    // Check for Google OAuth session_id in URL hash
    useEffect(() => {
        if (hasProcessed.current) return;
        
        const hash = window.location.hash;
        if (hash.includes('session_id=')) {
            hasProcessed.current = true;
            const sessionId = hash.split('session_id=')[1]?.split('&')[0];
            if (sessionId) {
                handleGoogleCallback(sessionId);
            }
        }
    }, []);

    // Redirect if already logged in
    useEffect(() => {
        if (user && !googleLoading) {
            navigate('/');
        }
    }, [user, googleLoading, navigate]);

    const handleGoogleCallback = async (sessionId) => {
        setGoogleLoading(true);
        try {
            await loginWithGoogle(sessionId);
            toast.success(t('auth.loginSuccess'));
            // Clear the hash from URL
            window.history.replaceState(null, '', window.location.pathname);
            navigate('/');
        } catch (error) {
            toast.error(error.message);
            window.history.replaceState(null, '', window.location.pathname);
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!loginData.email || !loginData.password) {
            toast.error(t('auth.fillAllFields'));
            return;
        }
        setLoading(true);
        try {
            await login(loginData.email, loginData.password);
            toast.success(t('auth.loginSuccess'));
            navigate('/');
        } catch (error) {
            const errorMessage = typeof error === 'object' && error.message ? error.message : 'Erreur de connexion';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!registerData.email || !registerData.password || !registerData.name) {
            toast.error(t('auth.fillAllFields'));
            return;
        }
        if (registerData.password.length < 6) {
            toast.error(t('auth.passwordMinLength'));
            return;
        }
        setLoading(true);
        try {
            await register(registerData.email, registerData.password, registerData.name);
            toast.success(t('auth.registerSuccess'));
            navigate('/');
        } catch (error) {
            const errorMessage = typeof error === 'object' && error.message ? error.message : 'Erreur d\'inscription';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/auth';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    if (googleLoading) {
        return (
            <div className="min-h-screen pt-20 flex items-center justify-center">
                <Card className="glass rounded-3xl w-full max-w-md mx-4">
                    <CardContent className="py-12 text-center">
                        <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto mb-4" />
                        <p className="text-muted-foreground">{t('auth.connecting')}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-20 pb-12 flex items-center justify-center" data-testid="auth-page">
            <div className="w-full max-w-md mx-4">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Droplets className="h-10 w-10 text-primary" />
                        <span className="font-heading font-bold text-2xl">O.C.A Piscine</span>
                    </div>
                    <p className="text-muted-foreground">{t('auth.yourExpert')}</p>
                </div>

                <Card className="glass rounded-3xl">
                    <Tabs defaultValue="login" className="w-full">
                        <CardHeader className="pb-2">
                            <TabsList className="grid w-full grid-cols-2 rounded-full h-12">
                                <TabsTrigger value="login" className="rounded-full" data-testid="login-tab">
                                    {t('auth.login')}
                                </TabsTrigger>
                                <TabsTrigger value="register" className="rounded-full" data-testid="register-tab">
                                    {t('auth.register')}
                                </TabsTrigger>
                            </TabsList>
                        </CardHeader>

                        <CardContent className="pt-4">
                            {/* Login Tab */}
                            <TabsContent value="login" className="space-y-4">
                                <form onSubmit={handleLogin} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="login-email">{t('auth.email')}</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="login-email"
                                                type="email"
                                                placeholder="votre@email.com"
                                                value={loginData.email}
                                                onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                                                className="pl-10 h-12 rounded-xl"
                                                data-testid="login-email"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="login-password">{t('auth.password')}</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="login-password"
                                                type="password"
                                                placeholder="••••••••"
                                                value={loginData.password}
                                                onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                                                className="pl-10 h-12 rounded-xl"
                                                data-testid="login-password"
                                            />
                                        </div>
                                    </div>
                                    <Button
                                        type="submit"
                                        className="w-full rounded-full h-12 font-semibold"
                                        disabled={loading}
                                        data-testid="login-btn"
                                    >
                                        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('auth.loginButton')}
                                    </Button>
                                </form>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-border" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-card px-2 text-muted-foreground">{t('common.or')}</span>
                                    </div>
                                </div>

                                <Button
                                    type="button"
                                    variant="secondary"
                                    className="w-full rounded-full h-12 font-semibold"
                                    onClick={handleGoogleLogin}
                                    data-testid="google-login-btn"
                                >
                                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                    </svg>
                                    {t('auth.googleLogin')}
                                </Button>

                                {/* Avertissement Google */}
                                <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                                    <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-muted-foreground">
                                        <strong className="text-amber-600 dark:text-amber-400">{t('auth.googleNotWorking')}</strong><br />
                                        {t('auth.googleWarningLogin')}
                                    </p>
                                </div>
                            </TabsContent>

                            {/* Register Tab */}
                            <TabsContent value="register" className="space-y-4">
                                <form onSubmit={handleRegister} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="register-name">{t('auth.name')}</Label>
                                        <div className="relative">
                                            <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="register-name"
                                                type="text"
                                                placeholder="Votre nom"
                                                value={registerData.name}
                                                onChange={(e) => setRegisterData(prev => ({ ...prev, name: e.target.value }))}
                                                className="pl-10 h-12 rounded-xl"
                                                data-testid="register-name"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="register-email">{t('auth.email')}</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="register-email"
                                                type="email"
                                                placeholder="votre@email.com"
                                                value={registerData.email}
                                                onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                                                className="pl-10 h-12 rounded-xl"
                                                data-testid="register-email"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="register-password">{t('auth.password')}</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="register-password"
                                                type="password"
                                                placeholder="••••••••"
                                                value={registerData.password}
                                                onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                                                className="pl-10 h-12 rounded-xl"
                                                data-testid="register-password"
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground">{t('auth.minPassword')}</p>
                                    </div>
                                    <Button
                                        type="submit"
                                        className="w-full rounded-full h-12 font-semibold"
                                        disabled={loading}
                                        data-testid="register-btn"
                                    >
                                        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('auth.createAccount')}
                                    </Button>
                                </form>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-border" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-card px-2 text-muted-foreground">{t('common.or')}</span>
                                    </div>
                                </div>

                                <Button
                                    type="button"
                                    variant="secondary"
                                    className="w-full rounded-full h-12 font-semibold"
                                    onClick={handleGoogleLogin}
                                    data-testid="google-register-btn"
                                >
                                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                    </svg>
                                    {t('auth.googleRegister')}
                                </Button>

                                {/* Avertissement Google */}
                                <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                                    <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-muted-foreground">
                                        <strong className="text-amber-600 dark:text-amber-400">{t('auth.googleNotWorking')}</strong><br />
                                        {t('auth.googleWarningRegister')}
                                    </p>
                                </div>
                            </TabsContent>
                        </CardContent>
                    </Tabs>
                </Card>
            </div>
        </div>
    );
}
